import { MapPin, Clock, Phone, Mail, Instagram, Facebook } from "lucide-react";
import { BRAND } from "../../lib/site";

export const Visit = () => {
  return (
    <section id="visit" className="relative py-20 md:py-32 bg-cream">
      <div className="container-narrow grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <span className="overline">Visit the boutique</span>
          <h2 className="mt-4 font-serif text-4xl md:text-6xl text-ink leading-[1.05]">
            Step inside our <span className="italic text-garnet">store</span>.
          </h2>
          <p className="mt-6 text-muteink leading-relaxed max-w-md">
            Nothing replaces the feel of silk between your fingers. Visit us —
            we'll set out the season's most beautiful drapes just for you.
          </p>

          <div className="mt-10 space-y-7">
            <div className="flex gap-4">
              <span className="shrink-0 w-10 h-10 border border-line flex items-center justify-center text-garnet">
                <MapPin size={18} strokeWidth={1.4} />
              </span>
              <div>
                <p className="overline">Address</p>
                <p className="mt-1 text-ink leading-relaxed">
                  New Varsha Sarees<br />
                  {BRAND.address.line1}<br />
                  {BRAND.address.line2}<br />
                  {BRAND.address.city}
                </p>
                <p className="mt-2 font-hindi text-sm text-muteink leading-relaxed">
                  {BRAND.address.hindi}
                </p>
                <a
                  href={BRAND.maps}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="visit-maps-link"
                  className="text-sm text-garnet underline-offset-4 hover:underline mt-3 inline-block"
                >
                  Get directions on Google Maps →
                </a>
              </div>
            </div>

            <div className="flex gap-4">
              <span className="shrink-0 w-10 h-10 border border-line flex items-center justify-center text-garnet">
                <Clock size={18} strokeWidth={1.4} />
              </span>
              <div>
                <p className="overline">Opening hours</p>
                <ul className="mt-1 space-y-0.5 text-ink">
                  {BRAND.hours.map((h) => (
                    <li key={h.day} className="text-sm">
                      <span className="text-muteink">{h.day} —</span> {h.time}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="flex gap-4">
              <span className="shrink-0 w-10 h-10 border border-line flex items-center justify-center text-garnet">
                <Phone size={18} strokeWidth={1.4} />
              </span>
              <div>
                <p className="overline">Call / WhatsApp</p>
                <a
                  href={`tel:+${BRAND.whatsappNumber}`}
                  className="mt-1 text-ink hover:text-garnet block"
                >
                  {BRAND.whatsappDisplay}
                </a>
              </div>
            </div>

            <div className="flex gap-4">
              <span className="shrink-0 w-10 h-10 border border-line flex items-center justify-center text-garnet">
                <Mail size={18} strokeWidth={1.4} />
              </span>
              <div>
                <p className="overline">Email</p>
                <a
                  href={`mailto:${BRAND.email}`}
                  data-testid="visit-email-link"
                  className="mt-1 text-ink hover:text-garnet block break-all"
                >
                  {BRAND.email}
                </a>
              </div>
            </div>

            <div className="flex gap-4 items-center pt-3">
              <a
                href={BRAND.instagram}
                target="_blank"
                rel="noreferrer"
                data-testid="visit-instagram-link"
                className="w-10 h-10 border border-line flex items-center justify-center text-garnet hover:bg-garnet hover:text-cream transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={16} strokeWidth={1.6} />
              </a>
              <a
                href={BRAND.facebook}
                target="_blank"
                rel="noreferrer"
                data-testid="visit-facebook-link"
                className="w-10 h-10 border border-line flex items-center justify-center text-garnet hover:bg-garnet hover:text-cream transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={16} strokeWidth={1.6} />
              </a>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <a
            href={BRAND.maps}
            target="_blank"
            rel="noreferrer"
            data-testid="visit-map-card"
            className="relative block aspect-[4/3] md:aspect-[5/4] overflow-hidden group"
          >
            <img
              src="https://images.unsplash.com/photo-1697896906714-ed5e1ad9b181?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
              alt="Boutique interior"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-garnet/70 via-black/30 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 md:bottom-8 md:left-8 md:right-8 flex items-end justify-between text-cream">
              <div>
                <p className="overline text-gold-soft">Find us</p>
                <p className="mt-2 font-serif text-3xl md:text-4xl">Open Google Maps</p>
              </div>
              <span className="px-5 py-2.5 border border-cream/60 text-[11px] tracking-[0.25em] uppercase group-hover:bg-cream group-hover:text-garnet transition-colors">
                View directions
              </span>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Visit;
